import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Icon } from "@qgiv/core-react";
import "./QuantityField.scss";
import Button from "../../Button";

const QuantityField = ({
    handleBlur = () => {},
    handleIncrementBtnClick = () => {},
    handleDecrementBtnClick = () => {},
    handleFocus = () => {},
    displayInventoryMessage,
    minusDisabled,
    minusAriaLabel,
    plusDisabled,
    plusAriaLabel,
    value,
    ...props
}) => {
    const ref = useRef();
    const [active, setActive] = useState(false);
    const [isChrome, setIsChrome] = useState(false);
    const [isSafari, setIsSafari] = useState(false);
    const userAgent =
        typeof window.navigator === "undefined" ? "" : navigator.userAgent;
    // NOTE - navigator.vendor is deprecated, but is only being used here to help us
    // detect the user's browser in order to fix a minor visual bug with the quantity icons on Safari.
    // If navigator.vendor is later removed from Safari, the only side-effect should be a reappearance
    // of the visual bug.
    const vendor = window.navigator === "undefined" ? "" : navigator.vendor;

    useEffect(() => {
        if (userAgent) {
            const hasSafariBeenDetected =
                /Safari/.test(userAgent) && /Apple Computer/.test(vendor);
            const hasChromeBeenDetected =
                /Chrome/.test(navigator.userAgent) && /Google Inc/.test(vendor);
            setIsSafari(hasSafariBeenDetected);
            setIsChrome(hasChromeBeenDetected);
        }
    }, []);

    // Helper that focuses the input DOM node
    const focusInputRef = () => {
        // if ref.current is an input element focus it, otherwise the element should be the input element to focus
        const element = ref.current.nodeType
            ? ref.current
            : ref.current.element;
        if (!active) {
            setActive(true);
        }
        element.focus();
    };
    const _onBlur = (e) => {
        setActive(false);
        handleBlur(e);
    };
    const _onFocus = (e) => {
        focusInputRef();
        handleFocus(e);
    };

    return (
        <div className="quantity-field">
            <div
                className={cx(
                    "quantity-field__inner",
                    active && "quantity-field__inner--active",
                )}
            >
                <div className="quantity-field__icon-container">
                    <Button
                        className="quantity-field__icon-space quantity-field__clear-quantity-btn -color--theme-primary -cursor--pointer"
                        type="button"
                        onClick={handleDecrementBtnClick}
                        aria-label={minusAriaLabel}
                    >
                        <div
                            className={cx(
                                "quantity-field__icon",
                                minusDisabled &&
                                    "quantity-field__icon--disabled",
                                isSafari &&
                                    !isChrome &&
                                    "quantity-field__icon--safari-only-transform",
                            )}
                        >
                            <Icon type="FontAwesome" glyph="minus" />
                        </div>
                    </Button>
                </div>
                <input
                    className="quantity-field__input"
                    type="number"
                    value={value}
                    ref={ref}
                    onBlur={_onBlur}
                    onFocus={_onFocus}
                    aria-label={`Product Quantity`}
                    {...props}
                />
                <div className="quantity-field__icon-container quantity-field__icon-container--align-right">
                    <Button
                        className="quantity-field__icon-space quantity-field__clear-quantity-btn -color--theme-primary -cursor--pointer"
                        type="button"
                        onClick={handleIncrementBtnClick}
                        aria-label={plusAriaLabel}
                    >
                        <div
                            className={cx(
                                "quantity-field__icon",
                                (plusDisabled || displayInventoryMessage) &&
                                    "quantity-field__icon--disabled",
                                isSafari &&
                                    !isChrome &&
                                    "quantity-field__icon--safari-only-transform",
                            )}
                        >
                            <Icon type="FontAwesome" glyph="plus-solid" />
                        </div>
                    </Button>
                </div>
            </div>
        </div>
    );
};

QuantityField.propTypes = {
    handleBlur: PropTypes.func,
    handleIncrementBtnClick: PropTypes.func,
    handleDecrementBtnClick: PropTypes.func,
    handleFocus: PropTypes.func,
    displayInventoryMessage: PropTypes.bool.isRequired,
    minusAriaLabel: PropTypes.string.isRequired,
    minusDisabled: PropTypes.bool.isRequired,
    plusAriaLabel: PropTypes.string.isRequired,
    plusDisabled: PropTypes.bool.isRequired,
    value: PropTypes.number.isRequired,
};

export default QuantityField;
